<template>
  <div v-loading="loading && !playing" class="nx-page kanban-device-alarm-chart-dynamic">
    <div class="dac-toolbar">
      <div>
        <span class="lab">刷新间隔</span>
        <el-select v-model="intervalMs" size="small" style="width: 120px" @change="onIntervalChange">
          <el-option :value="1500" label="1.5 秒" />
          <el-option :value="2500" label="2.5 秒" />
          <el-option :value="4000" label="4 秒" />
        </el-select>
      </div>
      <div>
        <span class="lab">时间粒度</span>
        <el-radio-group v-model="timeGranularity" size="small" @change="onGranularityChange">
          <el-radio-button label="hour">小时</el-radio-button>
          <el-radio-button label="day">日</el-radio-button>
          <el-radio-button label="week">周</el-radio-button>
        </el-radio-group>
      </div>
      <div>
        <span class="lab">展示模式</span>
        <el-select v-model="carouselMode" size="small" style="width: 220px" @change="applyCharts">
          <el-option value="trend" label="趋势 + 状态饼图" />
          <el-option value="level" label="级别柱状 + 类型玫瑰图" />
          <el-option value="mix" label="趋势联动高亮" />
        </el-select>
      </div>
      <el-button :type="playing ? 'info' : 'success'" size="small" @click="togglePlay">
        {{ playing ? '暂停刷新' : '开始动态刷新' }}
      </el-button>
      <span class="dac-pill" :class="{ on: playing }">{{ playing ? '刷新中' : '已暂停' }}</span>
      <span class="lab">已加载 {{ rawList.length }} 条</span>
      <span class="lab">当前口径 {{ periodLabel }}</span>
    </div>

    <div class="dac-charts">
      <div class="dac-chart-card full-row">
        <h3>{{ trendTitle }}</h3>
        <div ref="chartTrend" class="dac-chart-box tall" />
      </div>
      <div class="dac-chart-card">
        <h3>{{ pieTitle }}</h3>
        <div ref="chartPie" class="dac-chart-box" />
      </div>
      <div class="dac-chart-card">
        <h3>提醒级别 × 设备分类（堆叠柱）</h3>
        <div ref="chartStack" class="dac-chart-box" />
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import * as echarts from 'echarts'
import { fetchDeviceFaultAnalysis } from '@/views/kanban-management/device-fault-analysis/api'
import { STATUS_LABEL, TYPES, CATEGORIES, LEVELS } from '@/views/kanban-management/utils/mockDeviceAlarms'

const ANIM = 580
const CAT_COLORS = { 机电: '#3b82f6', 电气: '#a855f7', 配件: '#14b8a6' }
const GRANULARITY_CONFIG = {
  hour: { count: 24, unit: 'hour', title: '近 24 小时报警趋势', scopeLabel: '近 24 小时' },
  day: { count: 7, unit: 'day', title: '近 7 日报警趋势', scopeLabel: '近 7 日' },
  week: { count: 12, unit: 'week', title: '近 12 个自然周报警趋势', scopeLabel: '近 12 个自然周' }
}

export default {
  name: 'KanbanDeviceAlarmChartDynamic',
  data() {
    return {
      loading: false,
      rawList: [],
      intervalMs: 2500,
      timeGranularity: 'day',
      periodLabel: '近 7 日',
      carouselMode: 'trend',
      playing: false,
      pollTimer: null,
      tipTimer: null,
      tipTick: 0,
      trendTitle: '近 7 日报警趋势',
      pieTitle: '处理状态分布',
      chartTrend: null,
      chartPie: null,
      chartStack: null
    }
  },
  mounted() {
    this.loadData(false).then(() => {
      this.$nextTick(() => {
        this.ensureCharts()
        this.applyCharts()
      })
    })
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    this.stopPlay()
    this.disposeCharts()
  },
  methods: {
    normCategory(c) {
      return CATEGORIES.includes(c) ? c : null
    },
    granularityConfig() {
      return GRANULARITY_CONFIG[this.timeGranularity] || GRANULARITY_CONFIG.day
    },
    bucketStart(time, unit) {
      const m = moment(time)
      return unit === 'week' ? m.startOf('isoWeek') : m.startOf(unit)
    },
    bucketLabel(bucket, unit) {
      if (unit === 'hour') return bucket.start.format('MM/DD HH:00')
      if (unit === 'week') {
        return `${bucket.start.format('MM/DD')}-${bucket.end.clone().subtract(1, 'day').format('MM/DD')}`
      }
      return bucket.start.format('MM/DD')
    },
    alertMoment(alertTime) {
      if (alertTime === null || alertTime === undefined || alertTime === '') return null
      const raw = String(alertTime)
      const m = /^\d+$/.test(raw) ? moment(Number(raw)) : moment(alertTime)
      return m.isValid() ? m : null
    },
    timeBucketSeries(list) {
      const cfg = this.granularityConfig()
      const buckets = []
      const currentStart = this.bucketStart(moment(), cfg.unit)
      for (let i = cfg.count - 1; i >= 0; i--) {
        const start = currentStart.clone().subtract(i, cfg.unit === 'week' ? 'weeks' : cfg.unit)
        const end = start.clone().add(1, cfg.unit === 'week' ? 'week' : cfg.unit)
        buckets.push({
          start,
          end,
          key: start.valueOf(),
          count: 0
        })
      }
      const index = buckets.reduce((map, bucket) => {
        map[bucket.key] = bucket
        return map
      }, {})
      list.forEach((r) => {
        const m = this.alertMoment(r.alertTime)
        if (!m) return
        const key = this.bucketStart(m, cfg.unit).valueOf()
        if (index[key]) index[key].count++
      })
      return {
        labels: buckets.map((bucket) => this.bucketLabel(bucket, cfg.unit)),
        counts: buckets.map((bucket) => bucket.count),
        scopeStart: buckets[0] ? buckets[0].start.valueOf() : null,
        scopeEnd: buckets.length ? buckets[buckets.length - 1].end.valueOf() : null,
        scopeLabel: cfg.scopeLabel,
        trendTitle: cfg.title
      }
    },
    scopedList(list, series) {
      if (!series.scopeStart || !series.scopeEnd) return list
      return list.filter((r) => {
        const m = this.alertMoment(r.alertTime)
        if (!m) return false
        const t = m.valueOf()
        return t >= series.scopeStart && t < series.scopeEnd
      })
    },
    statusPieData(list) {
      const st = { 0: 0, 1: 0, 2: 0, 4: 0 }
      list.forEach((r) => {
        if (st[r.alertStatus] !== undefined) st[r.alertStatus]++
      })
      const colors = ['#f59e0b', '#3b82f6', '#94a3b8', '#22c55e']
      return [0, 1, 2, 4].map((k, i) => ({
        name: STATUS_LABEL[k],
        value: st[k],
        itemStyle: { color: colors[i] }
      })).filter((x) => x.value > 0)
    },
    typeRoseData(list) {
      const m = {}
      TYPES.forEach((t) => {
        m[t] = 0
      })
      list.forEach((r) => {
        const t = r.type
        if (m[t] !== undefined) m[t]++
      })
      const palette = ['#f87171', '#fb923c', '#38bdf8', '#94a3b8']
      return TYPES.map((name, i) => ({
        name,
        value: m[name] || 0,
        itemStyle: { color: palette[i] }
      })).filter((x) => x.value > 0)
    },
    stackOption(list) {
      const by = {}
      LEVELS.forEach((lv) => {
        by[lv] = { 机电: 0, 电气: 0, 配件: 0 }
      })
      list.forEach((r) => {
        const lv = r.alertType
        const c = this.normCategory(r.category)
        if (!LEVELS.includes(lv) || !c) return
        by[lv][c]++
      })
      return {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: {
          data: CATEGORIES,
          bottom: 0,
          textStyle: { color: '#64748b', fontSize: 11 }
        },
        grid: { left: 48, right: 16, top: 16, bottom: 48 },
        xAxis: {
          type: 'category',
          data: LEVELS,
          axisLine: { lineStyle: { color: '#cbd5e1' } },
          axisLabel: { color: '#64748b' }
        },
        yAxis: {
          type: 'value',
          minInterval: 1,
          splitLine: { lineStyle: { color: '#e2e8f0', type: 'dashed' } },
          axisLabel: { color: '#64748b' }
        },
        series: CATEGORIES.map((name) => ({
          name,
          type: 'bar',
          stack: 't',
          data: LEVELS.map((lv) => by[lv][name]),
          itemStyle: { color: CAT_COLORS[name] },
          animationDurationUpdate: ANIM
        })),
        animationDurationUpdate: ANIM
      }
    },
    ensureCharts() {
      if (this.chartTrend) return
      if (!this.$refs.chartTrend) return
      this.chartTrend = echarts.init(this.$refs.chartTrend)
      this.chartPie = echarts.init(this.$refs.chartPie)
      this.chartStack = echarts.init(this.$refs.chartStack)
    },
    disposeCharts() {
      [this.chartTrend, this.chartPie, this.chartStack].forEach((c) => {
        if (c) c.dispose()
      })
      this.chartTrend = this.chartPie = this.chartStack = null
    },
    onResize() {
      [this.chartTrend, this.chartPie, this.chartStack].forEach((c) => c && c.resize())
    },
    async loadData(silent) {
      if (!silent) this.loading = true
      try {
        const start = moment().startOf('day').subtract(120, 'day').valueOf()
        const res = await fetchDeviceFaultAnalysis({
          limit: 8000,
          startTime: start
        })
        this.rawList = (res && res.list) || []
        this.$nextTick(() => {
          this.ensureCharts()
          this.applyCharts()
        })
      } catch (e) {
        if (!silent) this.$message.error((e && e.message) || '加载失败')
      } finally {
        if (!silent) this.loading = false
      }
    },
    applyCharts() {
      this.ensureCharts()
      if (!this.chartTrend) return
      const list = this.rawList
      const timeSeries = this.timeBucketSeries(list)
      const { labels, counts } = timeSeries
      const scopedList = this.scopedList(list, timeSeries)
      this.periodLabel = timeSeries.scopeLabel
      const pieBase = this.statusPieData(scopedList)
      const pieData =
        pieBase.length > 0 ? pieBase : [{ name: '无数据', value: 1, itemStyle: { color: '#e2e8f0' } }]

      const commonText = { color: '#475569' }

      if (this.carouselMode === 'level') {
        this.trendTitle = `各级别报警条数（${timeSeries.scopeLabel}）`
        this.pieTitle = '报警类型分布（玫瑰图）'
        const byLv = {}
        LEVELS.forEach((l) => {
          byLv[l] = 0
        })
        scopedList.forEach((r) => {
          if (LEVELS.includes(r.alertType)) byLv[r.alertType]++
        })
        const lvCounts = LEVELS.map((l) => byLv[l])
        this.chartTrend.setOption(
          {
            backgroundColor: 'transparent',
            tooltip: { trigger: 'axis' },
            grid: { left: 48, right: 24, top: 28, bottom: 40 },
            xAxis: {
              type: 'category',
              data: LEVELS,
              axisLine: { lineStyle: { color: '#cbd5e1' } },
              axisLabel: { color: '#64748b' }
            },
            yAxis: {
              type: 'value',
              name: '条数',
              nameTextStyle: commonText,
              minInterval: 1,
              splitLine: { lineStyle: { color: '#e2e8f0', type: 'dashed' } },
              axisLabel: { color: '#64748b' }
            },
            series: [
              {
                name: '条数',
                type: 'bar',
                data: lvCounts,
                itemStyle: {
                  color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    { offset: 0, color: '#ef4444' },
                    { offset: 1, color: '#fecaca' }
                  ])
                },
                animationDurationUpdate: ANIM
              }
            ],
            animationDurationUpdate: ANIM
          },
          { notMerge: true }
        )
        const rose = this.typeRoseData(scopedList)
        const roseData =
          rose.length > 0 ? rose : [{ name: '无数据', value: 1, itemStyle: { color: '#e2e8f0' } }]
        this.chartPie.setOption(
          {
            backgroundColor: 'transparent',
            tooltip: { trigger: 'item' },
            legend: { bottom: 0, textStyle: { color: '#64748b', fontSize: 11 } },
            series: [
              {
                type: 'pie',
                roseType: 'radius',
                radius: ['25%', '70%'],
                center: ['50%', '46%'],
                data: roseData,
                label: { color: '#334155', fontSize: 11 },
                animationDurationUpdate: ANIM
              }
            ],
            animationDurationUpdate: ANIM
          },
          { notMerge: true }
        )
      } else if (this.carouselMode === 'mix') {
        this.trendTitle = `${timeSeries.trendTitle}（联动高亮）`
        this.pieTitle = '处理状态分布'
        const lineColor = '#0ea5e9'
        this.chartTrend.setOption(
          {
            backgroundColor: 'transparent',
            tooltip: { trigger: 'axis' },
            grid: { left: 48, right: 24, top: 28, bottom: 40 },
            xAxis: {
              type: 'category',
              data: labels,
              axisLine: { lineStyle: { color: '#cbd5e1' } },
              axisLabel: { color: '#64748b' }
            },
            yAxis: {
              type: 'value',
              name: '条数',
              nameTextStyle: commonText,
              minInterval: 1,
              splitLine: { lineStyle: { color: '#e2e8f0', type: 'dashed' } },
              axisLabel: { color: '#64748b' }
            },
            series: [
              {
                name: '报警数',
                type: 'line',
                smooth: true,
                data: counts,
                areaStyle: {
                  color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    { offset: 0, color: 'rgba(14,165,233,0.35)' },
                    { offset: 1, color: 'rgba(14,165,233,0.02)' }
                  ])
                },
                lineStyle: { width: 3, color: lineColor },
                itemStyle: { color: lineColor },
                animationDurationUpdate: ANIM
              }
            ],
            animationDurationUpdate: ANIM
          },
          { notMerge: true }
        )
        this.chartPie.setOption(
          {
            backgroundColor: 'transparent',
            tooltip: { trigger: 'item' },
            legend: { bottom: 0, textStyle: { color: '#64748b', fontSize: 11 } },
            series: [
              {
                type: 'pie',
                radius: ['42%', '72%'],
                center: ['50%', '46%'],
                data: pieData,
                label: { color: '#334155', fontSize: 11 },
                animationDurationUpdate: ANIM
              }
            ],
            animationDurationUpdate: ANIM
          },
          { notMerge: true }
        )
      } else {
        this.trendTitle = timeSeries.trendTitle
        this.pieTitle = '处理状态分布'
        this.chartTrend.setOption(
          {
            backgroundColor: 'transparent',
            tooltip: { trigger: 'axis' },
            grid: { left: 48, right: 24, top: 28, bottom: 40 },
            xAxis: {
              type: 'category',
              data: labels,
              axisLine: { lineStyle: { color: '#cbd5e1' } },
              axisLabel: { color: '#64748b' }
            },
            yAxis: {
              type: 'value',
              name: '条数',
              nameTextStyle: commonText,
              minInterval: 1,
              splitLine: { lineStyle: { color: '#e2e8f0', type: 'dashed' } },
              axisLabel: { color: '#64748b' }
            },
            series: [
              {
                name: '报警数',
                type: 'line',
                smooth: true,
                data: counts,
                areaStyle: {
                  color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    { offset: 0, color: 'rgba(220,38,38,0.35)' },
                    { offset: 1, color: 'rgba(220,38,38,0.02)' }
                  ])
                },
                lineStyle: { width: 3, color: '#ef4444' },
                itemStyle: { color: '#f87171' },
                animationDurationUpdate: ANIM
              }
            ],
            animationDurationUpdate: ANIM
          },
          { notMerge: true }
        )
        this.chartPie.setOption(
          {
            backgroundColor: 'transparent',
            tooltip: { trigger: 'item' },
            legend: { bottom: 0, textStyle: { color: '#64748b', fontSize: 11 } },
            series: [
              {
                type: 'pie',
                radius: ['42%', '72%'],
                center: ['50%', '46%'],
                data: pieData,
                label: { color: '#334155', fontSize: 11 },
                animationDurationUpdate: ANIM
              }
            ],
            animationDurationUpdate: ANIM
          },
          { notMerge: true }
        )
      }

      this.chartStack.setOption(this.stackOption(scopedList), { notMerge: true })

      if (this.carouselMode === 'mix' && this.playing) {
        this.scheduleTip(labels.length)
      } else {
        this.clearTipTimer()
        this.chartTrend.dispatchAction({ type: 'hideTip' })
      }
    },
    scheduleTip(len) {
      this.clearTipTimer()
      if (!len) return
      this.tipTimer = setInterval(() => {
        this.tipTick++
        if (this.chartTrend && this.carouselMode === 'mix') {
          this.chartTrend.dispatchAction({
            type: 'showTip',
            seriesIndex: 0,
            dataIndex: this.tipTick % len
          })
        }
      }, 1200)
    },
    clearTipTimer() {
      if (this.tipTimer) {
        clearInterval(this.tipTimer)
        this.tipTimer = null
      }
    },
    togglePlay() {
      if (this.playing) {
        this.stopPlay()
      } else {
        this.playing = true
        this.loadData(true)
        this.pollTimer = setInterval(() => this.loadData(true), this.intervalMs)
        if (this.carouselMode === 'mix') {
          this.$nextTick(() => {
            const { labels } = this.timeBucketSeries(this.rawList)
            this.scheduleTip(labels.length)
          })
        }
      }
    },
    stopPlay() {
      this.playing = false
      this.clearTipTimer()
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
      if (this.chartTrend) this.chartTrend.dispatchAction({ type: 'hideTip' })
    },
    onIntervalChange() {
      if (this.playing) {
        this.stopPlay()
        this.togglePlay()
      }
    },
    onGranularityChange() {
      this.applyCharts()
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>
